// Kyte ancillary resource module — id → label/icon/tooltip lookup,
// analogous to KYTE_AMENITIES. The unified ShopAncillaries response
// (kyte.ancillaries.shop) ships four ancillary slices — `bag`, `seat`,
// `sportsEquipment`, `service` — each as an object keyed by an
// ancillary id. Some ids are short uppercase codes (SKI, GOLF, PRBO),
// others are descriptive names ("ExcessBags3"). The catalog renderer
// reads `name` / `description` / `characteristics` straight off the
// raw entry today; this module gives every short code a localized
// label, an icon, and an optional tooltip the same way amenities do.
//
// Two shapes are exported:
//   KYTE_ANCILLARIES   — code → { txt, icon, tooltip }
//   KYTE_TYPE_ICONS    — canonical AdditionalService type → fallback icon
//                        (CHECKED_BAGS / SPORTS_EQUIPMENT / SERVICES /
//                        SEATS) for the post-book breakdown roll-up.
//
// Plus a resolver `get_kyte_ancillary_info(entry, type)` that returns
// `{ txt, icon, subtitle, tooltip }` ready for the catalog row.
// Consumers (kyte/bags.js, kyte/ancillaries.js) call it and never touch
// the raw maps directly.
//
// Classification primitives (TITLE_BY_GENDER, BUNDLE_SERVICE_CODES,
// SHOPPED_ANCILLARY_TYPES, OFFER_TYPE_FROM_KYTE, is_bundle_service,
// strip_pax_suffix) live below — pure data + tiny predicates that the
// orchestration file in ../ancillaries.js used to own.

import { infer_icon } from '@liveflight/resources/amenities';

// ─── Code → label dictionary ─────────────────────────────────────────────

export const KYTE_ANCILLARIES = {
  // ─── Bags ──────────────────────────────────────────────────────────────
  CBAG: {
    txt: 'Bagaglio in stiva 10 kg',
    icon: 'luggage',
    tooltip: '10 kg · 80×120×120 cm',
  },
  BBG: {
    txt: 'Bagaglio in stiva 20 kg',
    icon: 'luggage',
    tooltip: '20 kg · 80×120×120 cm',
  },
  BG23: {
    txt: 'Bagaglio in stiva 23 kg',
    icon: 'luggage',
    tooltip: '23 kg · 80×120×120 cm',
  },
  LUG: { txt: 'Bagaglio in stiva 23 kg', icon: 'luggage', tooltip: '23 kg' },
  LUS: { txt: 'Bagaglio in stiva 15 kg', icon: 'luggage', tooltip: '15 kg' },
  WGT: {
    txt: 'Peso in eccesso',
    icon: 'luggage',
    tooltip: 'Tariffa per kg aggiuntivo',
  },
  SCB1: {
    txt: 'Bagaglio cabina + imbarco prioritario',
    icon: 'trolley',
    tooltip: 'Bagaglio grande in cabina incluso imbarco prioritario',
  },
  'CABIN-10': {
    txt: 'Bagaglio cabina 10 kg + imbarco prioritario',
    icon: 'trolley',
    tooltip: '10 kg · con imbarco prioritario',
  },
  'CHECK-3': {
    txt: 'Bagaglio in stiva 3 kg',
    icon: 'luggage',
    tooltip: '3 kg',
  },
  'CHECK-5': {
    txt: 'Bagaglio in stiva 5 kg',
    icon: 'luggage',
    tooltip: '5 kg',
  },
  'CHECK-10': {
    txt: 'Bagaglio in stiva 10 kg',
    icon: 'luggage',
    tooltip: '10 kg',
  },
  'CHECK-15': {
    txt: 'Bagaglio in stiva 15 kg',
    icon: 'luggage',
    tooltip: '15 kg',
  },
  'CHECK-20': {
    txt: 'Bagaglio in stiva 20 kg',
    icon: 'luggage',
    tooltip: '20 kg',
  },
  'CHECK-25': {
    txt: 'Bagaglio in stiva 25 kg',
    icon: 'luggage',
    tooltip: '25 kg',
  },
  'CHECK-30': {
    txt: 'Bagaglio in stiva 30 kg',
    icon: 'luggage',
    tooltip: '30 kg',
  },
  'CHECK-40': {
    txt: 'Bagaglio in stiva 40 kg',
    icon: 'luggage',
    tooltip: '40 kg · fino a 5 colli, max 32 kg per collo',
  },
  'CHECK-50': {
    txt: 'Bagaglio in stiva 50 kg',
    icon: 'luggage',
    tooltip: '50 kg · fino a 5 colli, max 32 kg per collo',
  },
  'CHECK-20|count=2': {
    txt: '2 bagagli in stiva da 20 kg',
    icon: 'luggage',
    tooltip: '2 × 20 kg',
  },
  'CHECK-25|count=2': {
    txt: '2 bagagli in stiva da 25 kg',
    icon: 'luggage',
    tooltip: '2 × 25 kg',
  },
  'CHECK-INTL-8': {
    txt: 'Bagaglio in stiva 8 kg (connessione internazionale)',
    icon: 'luggage',
    tooltip: '8 kg · tariffa connessione internazionale',
  },
  'CHECK-INTL-15': {
    txt: 'Bagaglio in stiva 15 kg (connessione internazionale)',
    icon: 'luggage',
    tooltip: '15 kg · tariffa connessione internazionale',
  },
  'CHECK-INTL-30': {
    txt: 'Bagaglio in stiva 30 kg (connessione internazionale)',
    icon: 'luggage',
    tooltip: '30 kg · tariffa connessione internazionale',
  },
  'checked-in-bag-10|count=1': {
    txt: 'Bagaglio in stiva 10 kg',
    icon: 'luggage',
    tooltip: '10 kg',
  },
  'checked-in-bag-20|count=1': {
    txt: 'Bagaglio in stiva 20 kg',
    icon: 'luggage',
    tooltip: '20 kg',
  },
  'checked-in-bag-20|count=2': {
    txt: '2 bagagli in stiva da 20 kg',
    icon: 'luggage',
    tooltip: '2 × 20 kg',
  },
  'checked-in-bag-20|count=3': {
    txt: '3 bagagli in stiva da 20 kg',
    icon: 'luggage',
    tooltip: '3 × 20 kg',
  },
  'checked-in-bag-20|count=4': {
    txt: '4 bagagli in stiva da 20 kg',
    icon: 'luggage',
    tooltip: '4 × 20 kg',
  },
  'checked-in-bag-20|count=5': {
    txt: '5 bagagli in stiva da 20 kg',
    icon: 'luggage',
    tooltip: '5 × 20 kg',
  },
  'checked-in-bag-20|count=6': {
    txt: '6 bagagli in stiva da 20 kg',
    icon: 'luggage',
    tooltip: '6 × 20 kg',
  },
  'checked-in-bag-26|count=1': {
    txt: 'Bagaglio in stiva 26 kg',
    icon: 'luggage',
    tooltip: '26 kg',
  },
  'checked-in-bag-26|count=2': {
    txt: '2 bagagli in stiva da 26 kg',
    icon: 'luggage',
    tooltip: '2 × 26 kg',
  },
  'checked-in-bag-26|count=3': {
    txt: '3 bagagli in stiva da 26 kg',
    icon: 'luggage',
    tooltip: '3 × 26 kg',
  },
  'checked-in-bag-26|count=4': {
    txt: '4 bagagli in stiva da 26 kg',
    icon: 'luggage',
    tooltip: '4 × 26 kg',
  },
  'checked-in-bag-26|count=5': {
    txt: '5 bagagli in stiva da 26 kg',
    icon: 'luggage',
    tooltip: '5 × 26 kg',
  },
  'checked-in-bag-26|count=6': {
    txt: '6 bagagli in stiva da 26 kg',
    icon: 'luggage',
    tooltip: '6 × 26 kg',
  },
  'checked-in-bag-32|count=1': {
    txt: 'Bagaglio in stiva 32 kg',
    icon: 'luggage',
    tooltip: '32 kg',
  },
  'checked-in-bag-32|count=2': {
    txt: '2 bagagli in stiva da 32 kg',
    icon: 'luggage',
    tooltip: '2 × 32 kg',
  },
  'checked-in-bag-32|count=3': {
    txt: '3 bagagli in stiva da 32 kg',
    icon: 'luggage',
    tooltip: '3 × 32 kg',
  },
  'checked-in-bag-32|count=4': {
    txt: '4 bagagli in stiva da 32 kg',
    icon: 'luggage',
    tooltip: '4 × 32 kg',
  },
  'checked-in-bag-32|count=5': {
    txt: '5 bagagli in stiva da 32 kg',
    icon: 'luggage',
    tooltip: '5 × 32 kg',
  },
  'checked-in-bag-32|count=6': {
    txt: '6 bagagli in stiva da 32 kg',
    icon: 'luggage',
    tooltip: '6 × 32 kg',
  },
  HoldBaggage: {
    txt: 'Bagaglio in stiva 22 kg',
    icon: 'luggage',
    tooltip: '22 kg',
  },
  ManualWheelchair: {
    txt: 'Sedia a rotelle manuale',
    icon: 'accessibility',
    tooltip: 'Trasporto gratuito',
  },
  MotorisedWheelchair: {
    txt: 'Sedia a rotelle motorizzata',
    icon: 'accessibility',
    tooltip: 'Trasporto gratuito',
  },

  // ─── Sports equipment ──────────────────────────────────────────────────
  SKI: {
    txt: 'Sci',
    icon: 'ski',
    tooltip: 'Tariffa per articolo / per tratta · max 20 kg',
  },
  GOLF: {
    txt: 'Sacca da golf',
    icon: 'golf',
    tooltip: 'Tariffa per articolo / per tratta · max 20 kg',
  },
  BIKE: {
    txt: 'Bicicletta',
    icon: 'bike',
    tooltip: 'Tariffa per articolo / per tratta · max 30 kg',
  },
  SURF: {
    txt: 'Tavola da surf',
    icon: 'surfing',
    tooltip: 'Tariffa per articolo / per tratta · max 20 kg',
  },
  WIND: {
    txt: 'Tavola da windsurf',
    icon: 'surfing',
    tooltip: 'Tariffa per articolo / per tratta · max 20 kg',
  },
  SPRT: {
    txt: 'Attrezzatura sportiva grande',
    icon: 'sport',
    tooltip: 'Es. attrezzatura da windsurf · max 20 kg',
  },
  BULK: {
    txt: 'Altri articoli sportivi',
    icon: 'extra-bag',
    tooltip: 'Es. canne da pesca · max 20 kg',
  },
  SPEQ: {
    txt: 'Attrezzatura sportiva',
    icon: 'sport',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  SKBT: {
    txt: 'Sci e scarponi',
    icon: 'ski',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  SNBD: {
    txt: 'Snowboard',
    icon: 'ski',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  BICL: {
    txt: 'Bicicletta',
    icon: 'bike',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  WDSF: {
    txt: 'Windsurf',
    icon: 'surfing',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  CANO: {
    txt: 'Canoa / kayak',
    icon: 'sport',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  HGLD: {
    txt: 'Deltaplano',
    icon: 'sport',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  OSSE: {
    txt: 'Altra attrezzatura sportiva',
    icon: 'sport',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  FIRE: {
    txt: 'Armi da fuoco',
    icon: 'extra-bag',
    tooltip: 'Trasporto soggetto a normativa',
  },
  Bicycle: {
    txt: 'Bicicletta',
    icon: 'bike',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  Bodyboard: {
    txt: 'Bodyboard',
    icon: 'surfing',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  FishingRod: {
    txt: 'Canna da pesca',
    icon: 'extra-bag',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  GolfClubs: {
    txt: 'Sacca da golf',
    icon: 'golf',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  ScubaEquipment: {
    txt: 'Attrezzatura sub',
    icon: 'sport',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  Skis: {
    txt: 'Sci',
    icon: 'ski',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  Snowboard: {
    txt: 'Snowboard',
    icon: 'ski',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  Surf: {
    txt: 'Tavola da surf',
    icon: 'surfing',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  Windsurf: {
    txt: 'Windsurf',
    icon: 'surfing',
    tooltip: 'Tariffa per articolo / per tratta',
  },
  'sport-equipment': {
    txt: 'Attrezzatura sportiva',
    icon: 'sport',
    tooltip: 'Tariffa per articolo / per tratta · max 32 kg',
  },
  SKIS: {
    txt: 'Attrezzatura invernale',
    icon: 'ski',
    tooltip:
      '1 paio di sci, scarponi, bastoncini e casco oppure 1 snowboard con scarponi e casco',
  },
  DELT: {
    txt: 'Deltaplano',
    icon: 'sport',
    tooltip: 'Deltaplano nella sua custodia',
  },
  DIVE: {
    txt: 'Attrezzatura sub',
    icon: 'sport',
    tooltip: 'Attrezzatura subacquea non inclusa nel bagaglio regolare',
  },
  FISH: {
    txt: 'Attrezzatura da pesca',
    icon: 'extra-bag',
    tooltip: '3 canne, 1 paio di stivali, 1 cassetta porta-esca',
  },

  // ─── Disability assistance (IATA SSR codes) ────────────────────────────
  WCHS: {
    txt: 'Assistenza sedia a rotelle (scale)',
    icon: 'accessibility',
    tooltip: '',
  },
  WCHR: {
    txt: 'Assistenza sedia a rotelle (rampa)',
    icon: 'accessibility',
    tooltip: '',
  },
  WCHC: {
    txt: 'Assistenza sedia a rotelle (non ambulante)',
    icon: 'accessibility',
    tooltip: '',
  },
  BLND: { txt: 'Cieco', icon: 'accessibility', tooltip: '' },
  DEAF: { txt: 'Sordo', icon: 'accessibility', tooltip: '' },
  DPNA: {
    txt: 'Disabilità intellettiva',
    icon: 'accessibility',
    tooltip: 'Passeggero con disabilità intellettiva o disturbo dello sviluppo',
  },
  MEQT: {
    txt: 'Bagaglio medico',
    icon: 'accessibility',
    tooltip: 'Trasporto di apparecchiature mediche',
  },
  PPOC: {
    txt: 'Concentratore di ossigeno portatile',
    icon: 'accessibility',
    tooltip: 'Concentratore di ossigeno portatile a bordo',
  },
  SVAN: {
    txt: 'Animale di servizio in cabina',
    icon: 'pets',
    tooltip: 'Passeggero con animale di servizio in cabina',
  },
  WCMP: { txt: 'Sedia a rotelle manuale', icon: 'accessibility', tooltip: '' },
  WCBD: {
    txt: 'Sedia a rotelle elettrica (batteria a secco)',
    icon: 'accessibility',
    tooltip: '',
  },
  WCBW: {
    txt: 'Sedia a rotelle elettrica (batteria a liquido)',
    icon: 'accessibility',
    tooltip: '',
  },
  WCLB: {
    txt: 'Sedia a rotelle (batteria al litio)',
    icon: 'accessibility',
    tooltip: '',
  },
  SMBD: {
    txt: 'Scooter elettrico (batteria a secco)',
    icon: 'accessibility',
    tooltip: '',
  },
  SMBW: {
    txt: 'Scooter elettrico (batteria a liquido)',
    icon: 'accessibility',
    tooltip: '',
  },
  blind: { txt: 'Cieco', icon: 'accessibility', tooltip: '' },
  deaf: { txt: 'Sordo', icon: 'accessibility', tooltip: '' },
  'guide-dog': { txt: 'Cane guida', icon: 'pets', tooltip: '' },
  'need-assistance': {
    txt: 'Necessita assistenza',
    icon: 'accessibility',
    tooltip: '',
  },
  'wheelchair-manual': {
    txt: 'Sedia a rotelle manuale',
    icon: 'accessibility',
    tooltip: '',
  },
  'wheelchair-battery': {
    txt: 'Sedia a rotelle elettrica',
    icon: 'accessibility',
    tooltip: '',
  },
  'wheelchair-can-walk': {
    txt: 'Sedia a rotelle (autonomo a piedi)',
    icon: 'accessibility',
    tooltip: '',
  },
  'wheelchair-no-stairs': {
    txt: 'Sedia a rotelle (non può salire scale)',
    icon: 'accessibility',
    tooltip: '',
  },
  'wheelchair-immobile': {
    txt: 'Sedia a rotelle (non ambulante)',
    icon: 'accessibility',
    tooltip: '',
  },
  'wheelchair-fly-without-onboard-no': {
    txt: 'Sedia a rotelle non a bordo',
    icon: 'accessibility',
    tooltip: 'Sedia a rotelle in stiva, non a bordo',
  },
  'wheelchair-fly-without-onboard-yes': {
    txt: 'Sedia a rotelle a bordo',
    icon: 'accessibility',
    tooltip: 'Sedia a rotelle utilizzabile a bordo',
  },

  // ─── Services ──────────────────────────────────────────────────────────
  PS: {
    txt: 'Imbarco prioritario + 2 bagagli a mano',
    icon: 'star',
    tooltip:
      'Imbarco prioritario e 2 bagagli a mano (10 kg + 1 piccolo). Da aggiungere a tutti i passeggeri della prenotazione.',
  },
  FAST: {
    txt: 'Fast track sicurezza',
    icon: 'star',
    tooltip: 'Tariffa per tratta',
  },
  MUSC: {
    txt: 'Strumento musicale',
    icon: 'instruments',
    tooltip: 'Tariffa per articolo / per tratta · max 20 kg',
  },
  BUGY: {
    txt: 'Articolo di trasporto neonato/bambino',
    icon: 'stroller',
    tooltip:
      'Passeggino, seggiolino auto o lettino da viaggio · etichettato al gate o al check-in e imbarcato in stiva',
  },
  FXP: {
    txt: 'Flex Pass',
    icon: 'pen',
    tooltip:
      'Modifica volo senza costi aggiuntivi fino a 2 ore prima della partenza',
  },
  FFWD: {
    txt: 'Fast Forward',
    icon: 'star',
    tooltip: 'Check-in prioritario e imbarco in qualsiasi momento',
  },
  'airport-check-in': {
    txt: 'Carta d’Imbarco in Aereoporto',
    icon: 'flight_ticket',
    tooltip: "Ritira la tua carta d’imbarco stampata al banco del check-in",
  },
  'auto-check-in': {
    txt: 'Check-in automatico',
    icon: 'flight_ticket',
    tooltip:
      'Effettua automaticamente il check-in per te e i tuoi compagni di viaggio prima della partenza',
  },
  'online-check-in': {
    txt: 'Check-in online',
    icon: 'flight_ticket',
    tooltip: 'Effettua il check-in online prima della partenza',
  },
  'wizz-priority': {
    txt: 'Wizz Priority',
    icon: 'star',
    tooltip:
      'Check-in prioritario, imbarco prioritario, trolley (max 10 kg · 55 x 40 x 23 cm) e borsa a mano (40 x 30 x 20 cm)',
  },
  'wizz-flex': {
    txt: 'Wizz Flex',
    icon: 'pen',
    tooltip:
      'Cambio di volo online senza penali e rimborso sul conto WIZZ in caso di cancellazione della prenotazione',
  },
  FLEX: {
    txt: 'Flex ticket',
    icon: 'pen',
    tooltip:
      'Modifica data e ora del volo senza penali. Da acquistare per tutti i passeggeri.',
  },
  AVIH: {
    txt: 'Animale in stiva',
    icon: 'pets',
    tooltip: 'Trasporto animale domestico nel vano cargo',
  },
  PETC: {
    txt: 'Animale in cabina',
    icon: 'pets',
    tooltip: 'Trasporto animale domestico in cabina',
  },
};

const MEAL_CATEGORY_LABELS = {
  NonVegetarian: 'Con carne',
  Vegetarian: 'Vegetariano',
  Vegan: 'Vegano',
  GlutenFree: 'Senza glutine',
  ChildNonVegetarian: 'Menu bambini',
};

// Default icon per canonical AdditionalService `type` (post-mapping
// from Kyte's lowercase ancillary types via OFFER_TYPE_FROM_KYTE in
// kyte/ancillaries.js). The post-book roll-up reads from this when no
// per-entry icon is available — e.g. on retrieve / confirm where the
// catalog (with entry.icon) is no longer in scope.
export const KYTE_TYPE_ICONS = {
  CHECKED_BAGS: 'luggage',
  SPORTS_EQUIPMENT: 'sport',
  SERVICES: 'check_circle',
  SEATS: 'armchair',
  MEALS: 'restaurant',
  LOUNGE_ACCESS: 'star',
  DISABILITY_ASSISTANCE: 'accessibility',
};

// ─── Helpers ─────────────────────────────────────────────────────────────

/**
 * Case-fold a code and drop the shop `:segment` suffix and any `_RT`/`_OW`
 * roundtrip suffix, keeping `|count=N`.
 * @param {string} value
 * @returns {string}
 */
export const strip_meta = (value) =>
  String(value == null ? '' : value)
    .toLowerCase()
    .split(':')[0]
    .replace(/_(rt|ow)(?=\||$)/, '');

/**
 * Bare dictionary base of a code — `strip_meta` plus the `|count=N` suffix.
 * @param {string} value
 * @returns {string}
 */
export const base_code = (value) => strip_meta(value).split('|')[0];

// Every entry indexed twice: under its count-preserving form (multi-bag shop
// ids keep their "2 bagagli" label) and its bare base (count-less committed
// ids resolve). The singular entry wins the base slot.
const NORMALIZED_ANCILLARIES = Object.keys(KYTE_ANCILLARIES).reduce(
  (acc, key) => {
    const entry = KYTE_ANCILLARIES[key];
    const singular = !key.includes('|') || /\|count=1$/.test(key);
    if (!acc[strip_meta(key)]) acc[strip_meta(key)] = entry;
    if (!acc[base_code(key)] || singular) acc[base_code(key)] = entry;
    return acc;
  },
  {}
);

// Own-property lookup — a bare `map[key]` would resolve inherited members
// like `__proto__` / `toString` / `constructor` to truthy junk.
const dict_get = (map, key) =>
  key && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null;

/**
 * Resolve an ancillary entry to its dictionary record across every id shape
 * (shop, committed, roundtrip) without per-carrier maps; null when unmapped.
 * @param {{id?: string, name?: string}} entry
 * @returns {object|null}
 */
export const resolve_kyte_dict = (entry) => {
  const { id = '', name = '' } = entry || {};
  return (
    dict_get(KYTE_ANCILLARIES, id) ||
    dict_get(KYTE_ANCILLARIES, name) ||
    dict_get(NORMALIZED_ANCILLARIES, strip_meta(id)) ||
    dict_get(NORMALIZED_ANCILLARIES, base_code(id)) ||
    dict_get(NORMALIZED_ANCILLARIES, strip_meta(name)) ||
    dict_get(NORMALIZED_ANCILLARIES, base_code(name)) ||
    null
  );
};

/**
 * Readable last-resort label for an unmapped code, e.g. "checked-in-bag-26"
 * → "Checked In Bag 26", so a raw id never surfaces in the UI.
 * @param {string} value
 * @returns {string}
 */
export const humanize = (value) =>
  String(value == null ? '' : value)
    .replace(/\|count=\d+/i, '')
    .replace(/[-_]+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// ─── Contextual name discrimination (upgrade vs add) ─────────────────────

/**
 * Parse a bag ancillary id into its weight/count axes.
 * "checked-in-bag-26|count=2:W6-ORY-BUD" → { weight_kg: 26, count: 2 }.
 * @param {string} id
 * @returns {{weight_kg: number, count: number}}
 */
export const parse_bag_axes = (id) => {
  const base = strip_meta(id).split('|')[0];
  const weight = base.match(/(\d+)\s*$/);
  const count = String(id == null ? '' : id).match(/count=(\d+)/i);
  return {
    weight_kg: weight ? Number(weight[1]) : 0,
    count: count ? Number(count[1]) : 1,
  };
};

/**
 * Name-discrimination map — deliberately SEPARATE from KYTE_ANCILLARIES. On a
 * fare that already includes a checked bag, the carrier names each option by
 * its operation ("Additional 6kg of checked luggage" = weight upgrade,
 * "Additional checked-in bag 20 x1" = extra piece, "…x2 in total" =
 * upgrade + add). Each rule maps the matched name straight to the Italian
 * label that conveys the operation, so the UI renders it verbatim. A plain
 * "Checked in bag 26 x1" (no included bag) matches nothing here and keeps the
 * absolute dictionary label. WEIGHT_UPGRADE requires `additional` before the
 * weight so LCC names like EasyJet's "23kg Hold Bag" never trip it (verified
 * against live FR/U2 catalogs). Most specific first.
 * @type {Array<{match: RegExp, txt: (entry: object) => string}>}
 */
export const ANCILLARY_NAME_DISCRIMINATION = [
  {
    match: /\badditional\s+\d+\s*kg\b/i,
    txt: (entry) => {
      const { weight_kg } = parse_bag_axes((entry || {}).id);
      const delta = String((entry || {}).name || '').match(/(\d+)\s*kg/i);
      return `Upgrade bagaglio in stiva a ${weight_kg} kg (+${delta ? delta[1] : '?'} kg)`;
    },
  },
  {
    match: /\bin total\b/i,
    txt: (entry) => {
      const { weight_kg, count } = parse_bag_axes((entry || {}).id);
      return `${count} bagagli in stiva da ${weight_kg} kg in totale`;
    },
  },
  {
    match: /\badditional\s+checked-?in bag\b/i,
    txt: (entry) => {
      const { weight_kg, count } = parse_bag_axes((entry || {}).id);
      return count > 1
        ? `${count} bagagli in stiva aggiuntivi da ${weight_kg} kg`
        : `Bagaglio in stiva aggiuntivo da ${weight_kg} kg`;
    },
  },
];

/**
 * Italian label for a contextual ancillary `name` via
 * ANCILLARY_NAME_DISCRIMINATION, or '' when the name matches no rule (the
 * caller then falls back to the dictionary label).
 * @param {{id?: string, name?: string}} entry
 * @returns {string}
 */
export const discriminated_ancillary_label = (entry) => {
  const name = String((entry || {}).name || '');
  const rule = ANCILLARY_NAME_DISCRIMINATION.find((r) => r.match.test(name));
  return rule ? rule.txt(entry) : '';
};

// Stringify `characteristics.weight` / `characteristics.size` arrays
// into a single line for the subtitle. Both keys may be present — we
// join them with " · " so the row reads "23kg · 158cm".
const characteristic_label = (entry, key) => {
  const list = entry && entry.characteristics && entry.characteristics[key];
  return Array.isArray(list) && list.length ? list.join(' · ') : '';
};

const build_bag_subtitle = (entry) => {
  const weight = characteristic_label(entry, 'weight');
  const size = characteristic_label(entry, 'size');
  return [weight, size].filter(Boolean).join(' · ');
};

// ─── Public resolver ─────────────────────────────────────────────────────

// Resolve a Kyte ancillary catalog entry into the `{ txt, icon,
// subtitle, tooltip }` shape consumed by the catalog renderer. The
// `type` argument (`'bag'` | `'seat'` | `'sportsEquipment'` | `'service'`)
// drives subtitle behaviour and the per-type icon fallback.
//
//   1. Dictionary hit (id or name is a known code) → use its txt + icon.
//   2. No dictionary hit → use raw `description || name || id` as txt
//      and `infer_icon` on the title text for the icon. The `service`
//      type takes a `'star'` icon by default since service descriptions
//      rarely carry enough text to infer a meaningful icon.
//
// The shape mirrors `get_amenity_info` so consumers can treat amenity
// and ancillary rows interchangeably in the UI when needed.
export const get_kyte_ancillary_info = (entry, type) => {
  const safe = entry || {};
  const dict = resolve_kyte_dict(safe);
  const {
    txt: dict_txt = '',
    icon: dict_icon = '',
    tooltip: dict_tooltip = '',
  } = dict || {};

  // A contextual name (e.g. "Additional 6kg of checked luggage") carries the
  // upgrade-vs-add meaning that the absolute dictionary label would erase, so
  // its discrimination label wins; otherwise the dictionary (first map) label,
  // then the best-effort description/humanized-name fallback.
  const discriminated_txt = discriminated_ancillary_label(safe);
  const raw_title = safe.description || humanize(safe.name) || safe.id || '';
  const txt = discriminated_txt || dict_txt || raw_title;

  let icon;
  if (dict_icon) {
    icon = dict_icon;
  } else if (type === 'meal') {
    icon = KYTE_TYPE_ICONS.MEALS;
  } else if (type === 'loungeAccess') {
    icon = KYTE_TYPE_ICONS.LOUNGE_ACCESS;
  } else if (type === 'disabilityAssistance') {
    icon = KYTE_TYPE_ICONS.DISABILITY_ASSISTANCE;
  } else if (type === 'service') {
    icon = 'star';
  } else if (type === 'sportsEquipment') {
    icon = KYTE_TYPE_ICONS.SPORTS_EQUIPMENT;
  } else {
    icon = infer_icon({ code: '', description: txt });
  }

  let subtitle;
  if (type === 'meal') {
    subtitle = MEAL_CATEGORY_LABELS[safe.name] || '';
  } else if (
    type === 'bag' ||
    type === 'sportsEquipment' ||
    type === 'service'
  ) {
    // Dictionary tooltip wins over the upstream description so curated
    // Italian copy replaces the raw English text in catalog rows.
    subtitle =
      build_bag_subtitle(safe) ||
      (type !== 'bag' ? dict_tooltip || safe.description || '' : '');
  } else {
    subtitle = '';
  }

  return {
    txt,
    icon,
    subtitle,
    tooltip: dict_tooltip,
  };
};

// ─── Classification primitives ───────────────────────────────────────────
// Pure data + predicates lifted out of ../ancillaries.js so the
// orchestration file owns book/fetch/diff only.

/**
 * Kyte BookFlight passenger `title` mapping. Kyte expects the
 * lowercase salutation token; the form keeps gender uppercase.
 * @type {Object<string,string>}
 */
export const TITLE_BY_GENDER = {
  MALE: 'mr',
  FEMALE: 'mrs',
};

/**
 * Capability → ShopAncillaries wiring, one row per shoppable type. Drives
 * both the request payload and the response normalization from a single
 * table so "what we ask Kyte for" and "what we build" can't drift.
 *   capability   gate flag on KyteProvider (kyte/index.js)
 *   request      shopAncillariesRequest.requestedTypes token — Kyte's
 *                AncillaryType enum (docs: ShopAncillariesRequest)
 *   sourceKey    slice key on the raw ShopAncillaries response
 *   envelopeKey  slice key on the normalized envelope /ancillaries reads
 *   catalogType  type passed to build_extra_bags_catalog; absent for `seat`,
 *                which build_seatmap_envelope handles instead
 * `bundle` is intentionally omitted — fare-bundle premiums ride with the
 * fare (BUNDLE_SERVICE_CODES) and are never shopped as a standalone extra.
 *
 * @type {Array<{capability: string, request: string, sourceKey: string, envelopeKey: string, catalogType?: string}>}
 */
export const KYTE_ANCILLARY_CAPABILITIES = [
  {
    capability: 'shopBags',
    request: 'bag',
    sourceKey: 'bags',
    envelopeKey: 'bag',
    catalogType: 'bag',
  },
  {
    capability: 'shopSeats',
    request: 'seat',
    sourceKey: 'seatMap',
    envelopeKey: 'seat',
  },
  {
    capability: 'shopServices',
    request: 'service',
    sourceKey: 'services',
    envelopeKey: 'service',
    catalogType: 'service',
  },
  {
    capability: 'shopSportsEquipment',
    request: 'sportsEquipment',
    sourceKey: 'sportsEquipment',
    envelopeKey: 'sportsEquipment',
    catalogType: 'sportsEquipment',
  },
  {
    capability: 'shopMeals',
    request: 'meal',
    sourceKey: 'meals',
    envelopeKey: 'meal',
    catalogType: 'meal',
  },
  {
    capability: 'shopLoungeAccess',
    request: 'loungeAccess',
    sourceKey: 'loungeAccess',
    envelopeKey: 'loungeAccess',
    catalogType: 'loungeAccess',
  },
  {
    capability: 'shopDisabilityAssistance',
    request: 'disabilityAssistance',
    sourceKey: 'disabilityAssistance',
    envelopeKey: 'disabilityAssistance',
    catalogType: 'disabilityAssistance',
  },
];

/**
 * Ancillary types eligible for the shopped roll-up. Service rows are
 * included here but bundle-code services are filtered out downstream
 * via is_bundle_service.
 *
 * @type {Set<string>}
 */
export const SHOPPED_ANCILLARY_TYPES = new Set([
  'bag',
  'seat',
  'sportsEquipment',
  'service',
  'meal',
  'loungeAccess',
  'disabilityAssistance',
]);

/**
 * True when a pricingBreakdown row is an ancillary. Retrieve rows carry
 * `category:'ancillary'`; shop per-pax rows carry only a `type`. Fare/tax rows
 * are never ancillaries.
 *
 * @param {{category?: string, type?: string}} row
 * @returns {boolean}
 */
export const is_ancillary_row = (row) => {
  const { category = '', type = '' } = row || {};
  if (typeof category === 'string' && category.toLowerCase() === 'ancillary') {
    return true;
  }
  return SHOPPED_ANCILLARY_TYPES.has(type);
};

/**
 * Fare-bundle premium ids Kyte ships under `type:'service'`. These ride
 * with the chosen fare (REGU/PLUS/Family/SURE on Ryanair, equivalents
 * on other carriers) and stay in offer.price.total — never as a shopped
 * extra. Shopped services (PS/FAST/MUSC/BUGY/…) are not in this set and
 * surface in the SERVICES rollup.
 *
 * @type {Set<string>}
 */
export const BUNDLE_SERVICE_CODES = new Set([
  'REGU',
  'PLUS',
  'FAMILY',
  'FAMILYPLUS',
  'SURE',
  // easyJet's unpriced echoes of a shopped item, never shopped themselves:
  // B0017 rides with Flex Pass (FXP), B0006/B0007 with SCB1.
  'B0006',
  'B0007',
  'B0017',
]);

// Never offered for sale: easyJet excess weight can't stand alone and only
// cascades picks onto the paid hold bags.
export const NOT_SOLD_ANCILLARY_CODES = new Set(['WGT', 'WGT_RT']);

/**
 * Map Kyte's lowercase ancillary `type` to the canonical Amadeus-style
 * label used in FlightPrice.additionalServices. Types outside this map
 * are dropped from the roll-up. Bundle service ids are filtered
 * separately via is_bundle_service so they stay in fare price.
 *
 * @type {Object<string,string>}
 */
export const OFFER_TYPE_FROM_KYTE = {
  bag: 'CHECKED_BAGS',
  seat: 'SEATS',
  sportsEquipment: 'SPORTS_EQUIPMENT',
  service: 'SERVICES',
  meal: 'MEALS',
  loungeAccess: 'LOUNGE_ACCESS',
  disabilityAssistance: 'DISABILITY_ASSISTANCE',
};

/**
 * Strip Kyte's pax suffix from a pricingBreakdown id (e.g. "PS-1" →
 * "PS"). booking.ancillaries[].id is the bare code; pricingBreakdown
 * rows append `-N` for the pax index. The (passengerId, type, bareId)
 * triple is what enrich_with_breakdown_price keys on.
 *
 * @param {string} raw_id
 * @returns {string}
 */
export const strip_pax_suffix = (raw_id) =>
  String(raw_id || '').replace(/-\d+$/, '');

/**
 * @param {string} type Kyte ancillary type
 * @param {string} raw_id pricingBreakdown id (may be pax-suffixed)
 * @returns {boolean} true when the row is a fare-bundle premium
 */
export const is_bundle_service = (type, raw_id) =>
  type === 'service' &&
  BUNDLE_SERVICE_CODES.has(strip_pax_suffix(raw_id).toUpperCase());
